import React from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.14, delayChildren: 0.2 },
  },
};
const item = {
  hidden: { opacity: 0, y: 24, filter: "blur(6px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  const scrollDown = () => {
    const el = document.getElementById("countdown");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex flex-col items-center justify-center px-6 pt-12 pb-10 text-center overflow-hidden"
    >
      {/* Decorative slow-spin ring behind names */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none"
      >
        <div className="slow-spin w-[340px] h-[340px] sm:w-[420px] sm:h-[420px] rounded-full border border-champagne/25" />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 2.2, delay: 0.3 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none"
      >
        <div className="w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] ink-blob-1 bg-gradient-to-br from-champagne/10 via-rose/10 to-ink/5 backdrop-blur-sm" />
      </motion.div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 max-w-md"
      >
        {/* Bismillah */}
        <motion.p
          variants={item}
          data-testid="bismillah"
          className="font-arabic text-2xl sm:text-3xl text-ink/80 leading-loose mb-6 select-none"
        >
          بِسْمِ ٱللَّٰهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
        </motion.p>

        {/* Small label */}
        <motion.p
          variants={item}
          className="uppercase tracking-[0.45em] text-[11px] sm:text-xs text-ink/60 mb-6"
        >
          Together with their families
        </motion.p>

        {/* Names */}
        <div data-testid="couple-names">
          <motion.h1
            variants={item}
            data-testid="couple-name-fuzail"
            className="font-display text-ink text-[3rem] sm:text-6xl"
          >
            Fuzail
          </motion.h1>

          <motion.div
            variants={item}
            className="flex items-center justify-center gap-5 my-5"
          >
            <span className="h-px flex-1 bg-champagne/40" />
            <span className="font-display text-champagne text-2xl">
              &amp;
            </span>
            <span className="h-px flex-1 bg-champagne/40" />
          </motion.div>

          <motion.h1
            variants={item}
            data-testid="couple-name-fayeza"
            className="font-display text-ink text-[3rem] sm:text-6xl"
          >
            Fayeza
          </motion.h1>
        </div>

        {/* Date */}
        <motion.div variants={item} className="mt-10">
          <p className="font-serif text-lg sm:text-xl tracking-[0.3em] text-ink/80 uppercase">
            16 · 08 · 2026
          </p>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        type="button"
        onClick={scrollDown}
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.2, delay: 1.6 }}
        className="absolute bottom-6 inset-x-0 mx-auto w-fit z-10 flex flex-col items-center gap-2 text-ink/60 hover:text-ink transition-colors"
        aria-label="Scroll to countdown"
      >
        <span className="text-[10px] tracking-[0.35em] uppercase">Scroll</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={18} strokeWidth={1.4} />
        </motion.span>
      </motion.button>
    </section>
  );
}
